import { ArrowLeft, ArrowRight, FileArchive, Send, Trash2 } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import type { BulkTask } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { useBottomLayer } from '../../../lib/hooks';
import { Button } from '../../../ui/Button';
import salesStyles from '../Sales.module.css';
import styles from './Batch.module.css';
import { runLabel } from './text';

const RUN_ICON = {
  send: <Send size={16} aria-hidden="true" />,
  pdf: <FileArchive size={16} aria-hidden="true" />,
  discard: <Trash2 size={16} aria-hidden="true" />,
} as const;

/**
 * The step bar docked at the bottom (BLOCK-1B-BULK-DESIGN §1; the editor's pattern): «رجوع» (steps
 * 2–3), «متابعة إلى …» (steps 1–2) and, on the review step only and only when at least one document
 * will run, the run button named by the task. No count, no status text, no «إفراغ الحزمة» (AC-B4,
 * AC-B5). On phones «رجوع» becomes a named icon and the main button takes the width.
 */
export function BatchBar({
  backTo,
  nextTo,
  onBack,
  onNext,
  run,
  onRun,
  isBusy,
  children,
}: {
  /** Name of the previous step, or null on the first. */
  backTo: string | null;
  /** Name of the next step, or null on the review. */
  nextTo: string | null;
  onBack: () => void;
  onNext: () => void;
  /** The task whose run button shows, or null (not the review, or nothing will run). */
  run: BulkTask | null;
  onRun: () => void;
  isBusy: boolean;
  /**
   * A layer that rides on the bar, just above it at every width (the undo bar, RIB-06): one bottom
   * layer at a time, and the two never overlap.
   */
  children?: ReactNode;
}) {
  const { t } = useI18n();
  const barRef = useRef<HTMLDivElement>(null);
  useBottomLayer(barRef);
  return (
    <div ref={barRef} className={salesStyles.actionBar} data-testid="batch-bar">
      {children ? <div className={styles.barLayer}>{children}</div> : null}
      <div className={salesStyles.stepActions}>
        {backTo ? (
          <Button
            variant="ghost"
            icon={<ArrowLeft size={16} className="flip-rtl" aria-hidden="true" />}
            onPress={onBack}
            isDisabled={isBusy}
            testId="batch-back"
            aria-label={t.editor.back}
            className={salesStyles.stepBack}
          >
            <span className={salesStyles.stepBarText}>{t.editor.back}</span>
          </Button>
        ) : null}
        {nextTo ? (
          <Button
            variant="primary"
            onPress={onNext}
            isDisabled={isBusy}
            testId="batch-next"
            className={salesStyles.stepNext}
          >
            {t.editor.next(nextTo)}
            <ArrowRight size={16} className="flip-rtl" aria-hidden="true" />
          </Button>
        ) : run ? (
          <Button
            variant={run === 'discard' ? 'danger' : 'primary'}
            icon={RUN_ICON[run]}
            onPress={onRun}
            isPending={isBusy}
            testId="batch-run"
            data-task={run}
            className={salesStyles.stepNext}
          >
            {runLabel(t, run)}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
